import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Plus } from "lucide-react";
import { useCreatePromotion } from "@/hooks/usePromotions";
import { format } from "date-fns";
import DaysOfWeekSelector from "./DaysOfWeekSelector";

interface Service {
  id: string;
  name: string;
}

interface AddPromotionDialogProps {
  providerId: string;
  services: Service[];
  onPromotionAdded: () => void;
}

const AddPromotionDialog = ({ providerId, services, onPromotionAdded }: AddPromotionDialogProps) => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    service_id: "",
    discount_type: "percentage" as "percentage" | "amount",
    discount_percentage: "",
    discount_amount: "",
    start_date: "",
    end_date: "",
    days_of_week: [0, 1, 2, 3, 4, 5, 6] as number[],
    is_active: true,
  });

  const createPromotion = useCreatePromotion();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('Raw form data:', {
      discount_type: formData.discount_type,
      discount_percentage: formData.discount_percentage,
      discount_amount: formData.discount_amount,
      name: formData.name,
      service_id: formData.service_id
    });
    
    const discountValue = formData.discount_type === 'percentage' ? formData.discount_percentage : formData.discount_amount;
    
    if (!formData.name || !formData.service_id || !discountValue || 
        !formData.start_date || !formData.end_date || formData.days_of_week.length === 0) {
      console.error('Form validation failed:', {
        name: !formData.name,
        service_id: !formData.service_id,
        discountValue: !discountValue,
        start_date: !formData.start_date,
        end_date: !formData.end_date,
        days_of_week: formData.days_of_week.length === 0
      });
      return;
    }

    // Validate and parse discount values
    let discountPercentage = null;
    let discountAmount = null;

    if (formData.discount_type === 'percentage') {
      const parsed = parseFloat(formData.discount_percentage);
      console.log('Parsing percentage:', formData.discount_percentage, 'Result:', parsed);
      
      if (isNaN(parsed) || parsed <= 0 || parsed > 100) {
        console.error('Invalid percentage value:', parsed);
        return;
      }
      discountPercentage = parsed;
    } else {
      const parsed = parseFloat(formData.discount_amount);
      console.log('Parsing amount:', formData.discount_amount, 'Result:', parsed);
      
      if (isNaN(parsed) || parsed <= 0) {
        console.error('Invalid amount value:', parsed);
        return;
      }
      discountAmount = parsed;
    }

    const promotionData = {
      provider_id: providerId,
      service_id: formData.service_id,
      name: formData.name,
      description: formData.description || undefined,
      discount_type: formData.discount_type,
      discount_percentage: discountPercentage,
      discount_amount: discountAmount,
      start_date: formData.start_date,
      end_date: formData.end_date,
      days_of_week: formData.days_of_week,
      is_active: formData.is_active,
    };

    console.log('Final promotion data to submit:', promotionData);

    try {
      await createPromotion.mutateAsync(promotionData);

      setFormData({
        name: "",
        description: "",
        service_id: "",
        discount_type: "percentage",
        discount_percentage: "",
        discount_amount: "",
        start_date: "",
        end_date: "",
        days_of_week: [0, 1, 2, 3, 4, 5, 6],
        is_active: true,
      });
      setOpen(false);
      onPromotionAdded();
    } catch (error) {
      console.error('Error creating promotion:', error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          Add Promotion
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Create New Promotion</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="name">Promotion Name</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Winter Special"
              required
            />
          </div>

          <div>
            <Label htmlFor="description">Description (Optional)</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe your promotion..."
              rows={2}
            />
          </div>

          <div>
            <Label htmlFor="service">Service</Label>
            <Select
              value={formData.service_id}
              onValueChange={(value) => setFormData({ ...formData, service_id: value })}
              required
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a service" />
              </SelectTrigger>
              <SelectContent>
                {services.map((service) => (
                  <SelectItem key={service.id} value={service.id}>
                    {service.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="discount_type">Discount Type</Label>
            <Select
              value={formData.discount_type}
              onValueChange={(value: "percentage" | "amount") => setFormData({ 
                ...formData, 
                discount_type: value,
                discount_percentage: "",
                discount_amount: ""
              })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="percentage">Percentage Off (%)</SelectItem>
                <SelectItem value="amount">Fixed Amount Off (£)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="discount_value">
              {formData.discount_type === 'percentage' ? 'Discount Percentage' : 'Discount Amount (£)'}
            </Label>
            <Input
              id="discount_value"
              type="number"
              min="1"
              max={formData.discount_type === 'percentage' ? "100" : undefined}
              step={formData.discount_type === 'amount' ? "0.01" : "1"}
              value={formData.discount_type === 'percentage' ? formData.discount_percentage : formData.discount_amount}
              onChange={(e) => setFormData({ 
                ...formData, 
                [formData.discount_type === 'percentage' ? 'discount_percentage' : 'discount_amount']: e.target.value 
              })}
              placeholder={formData.discount_type === 'percentage' ? "e.g., 25" : "e.g., 5.00"}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="start_date">Start Date</Label>
              <Input
                id="start_date"
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                min={format(new Date(), 'yyyy-MM-dd')}
                required
              />
            </div>
            <div>
              <Label htmlFor="end_date">End Date</Label>
              <Input
                id="end_date"
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                min={formData.start_date || format(new Date(), 'yyyy-MM-dd')}
                required
              />
            </div>
          </div>

          <DaysOfWeekSelector
            selectedDays={formData.days_of_week}
            onDaysChange={(days) => setFormData({ ...formData, days_of_week: days })}
          />

          <div className="flex items-center space-x-2">
            <Switch
              id="is_active"
              checked={formData.is_active}
              onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
            />
            <Label htmlFor="is_active">Active</Label>
          </div>

          <div className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={createPromotion.isPending}>
              {createPromotion.isPending ? "Creating..." : "Create Promotion"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddPromotionDialog;